import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Alert, Empty, Spin, Tooltip, Typography, Divider, Space, Button, Select, Radio } from "antd";
import { FilterOutlined, ReloadOutlined, WarningTwoTone, PlusOutlined, MinusOutlined, ClockCircleOutlined, ThunderboltOutlined, EyeOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import minMax from "dayjs/plugin/minMax";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import timezone from "dayjs/plugin/timezone";
import moment from "moment-timezone";

import { Timeline } from "vis-timeline/esnext";
import { DataSet } from "vis-data/esnext";
import "vis-timeline/styles/vis-timeline-graph2d.css";

import { useMonitor } from "../context/MonitorContext";
import useTimezoneConverter from "../../../../hooks/useTimezoneConverter";
import { FLIGHT_STATUS, FLIGHT_STATUS_COLORS, FLIGHT_STATUS_LABELS } from "../constants/flightConstants";
import { MODAL_KEYS } from "../constants";
import { resolveVisTimelineTimezone, shouldForceRecreateTimelineForTimezone, resolveTimelineWindowOnRecreate } from "./timelineTimezone";
import { resolveForwardTimelineTimes, resolveTimelineOverlapIds } from "./timelineOverlap";
import "./FlightTimelineView.css";

const { Text } = Typography;
const { Option } = Select;

dayjs.extend(utc);
dayjs.extend(minMax);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(timezone);

const TIMELINE_VIEW_MODES = {
    BOTH: "both",
    PLAN_ONLY: "plan",
    ACTUAL_ONLY: "actual",
};

const TIMELINE_VIEW_MODE_LABELS = {
    [TIMELINE_VIEW_MODES.BOTH]: "双行视图",
    [TIMELINE_VIEW_MODES.PLAN_ONLY]: "仅计划时间",
    [TIMELINE_VIEW_MODES.ACTUAL_ONLY]: "仅执行时间",
};

/**
 * 航班时间轴视图组件（按飞机）
 * 使用 vis-timeline 构建横向航班时间轴，每行代表一架飞机
 */
const FlightTimelineView = () => {
    const { flights, loading, toggleModal, pagination } = useMonitor();
    const { currentTimezone } = useTimezoneConverter();

    const [selectedAircraft, setSelectedAircraft] = useState(["ALL"]);
    const [visError, setVisError] = useState(null);
    const [timelineReady, setTimelineReady] = useState(false);
    const [timelineViewMode, setTimelineViewMode] = useState(TIMELINE_VIEW_MODES.BOTH);

    // DataSet 是可变容器，用 ref 而非 state 持有
    const timelineItems = useRef(new DataSet([]));
    const timelineGroups = useRef(new DataSet([]));
    const timelineContainer = useRef(null);
    const timelineInstance = useRef(null);
    const pendingTimers = useRef(new Set());
    const appliedTimezoneRef = useRef(null);

    // 安全的 setTimeout 包装：自动跟踪并在卸载时清理
    const safeTimeout = useCallback((fn, delay) => {
        const id = setTimeout(() => {
            pendingTimers.current.delete(id);
            fn();
        }, delay);
        pendingTimers.current.add(id);
        return id;
    }, []);

    // 数据是否被截断
    const isDataTruncated = pagination && pagination.total > flights.length;

    const filteredFlights = useMemo(() => {
        if (selectedAircraft.includes("ALL")) return flights;
        return flights.filter((flight) => selectedAircraft.includes(flight.aircraft_reg || "UNASSIGNED"));
    }, [flights, selectedAircraft]);

    const availableAircraft = useMemo(() => {
        const aircraftSet = new Set(flights.map((f) => f.aircraft_reg).filter(Boolean));
        const aircraftList = Array.from(aircraftSet).sort();
        if (flights.some((f) => !f.aircraft_reg)) {
            return [{ id: "UNASSIGNED", name: "未分配飞机" }, ...aircraftList.map((reg) => ({ id: reg, name: reg }))];
        }
        return aircraftList.map((reg) => ({ id: reg, name: reg }));
    }, [flights]);

    const createFlightTooltip = useCallback(
        (flight, isOverlapping = false, timelineType = "plan") => {
            const flightStatus = flight.status || FLIGHT_STATUS.SCHEDULED;
            const statusLabel = FLIGHT_STATUS_LABELS[flightStatus] || flightStatus;
            // Depend on currentTimezone (stable string), not fromUTC identity which changes every render
            const formatInCurrentZone = (utcTime, format = "MM-DD HH:mm") => {
                if (!utcTime) return "--:--";
                try {
                    return dayjs.utc(utcTime).tz(currentTimezone).format(format);
                } catch (error) {
                    console.error("时区转换错误:", error);
                    return "--:--";
                }
            };

            let tooltip = `<div class="vis-tooltip-content">`;
            tooltip += `<div><strong>航班:</strong> ${flight.flight_number} (${flight.departure_airport} → ${flight.arrival_airport})</div>`;
            tooltip += `<div><strong>注册号:</strong> ${flight.aircraft_reg || "未分配"}</div>`;
            tooltip += `<div><strong>状态:</strong> ${statusLabel}</div>`;

            if (timelineType === "plan") {
                tooltip += `<div><strong>时间轴:</strong> 计划时间</div>`;
            } else {
                tooltip += `<div><strong>时间轴:</strong> 执行时间</div>`;
            }

            if (isOverlapping) {
                tooltip += `<div class="vis-tooltip-warning"><strong>时间冲突:</strong> 此航班与同飞机其他航班时间重叠</div>`;
            }

            tooltip += `<hr>`;

            if (timelineType === "plan") {
                if (flight.etd || flight.eta) {
                    tooltip += `<div><strong>预计起飞 (ETD):</strong> ${formatInCurrentZone(flight.etd)}</div>`;
                    tooltip += `<div><strong>预计到达 (ETA):</strong> ${formatInCurrentZone(flight.eta)}</div>`;
                    tooltip += `<hr><div style="font-size: 11px; color: #666;">对比计划时间:</div>`;
                }
                tooltip += `<div><strong>计划起飞 (STD):</strong> ${formatInCurrentZone(flight.std)}</div>`;
                tooltip += `<div><strong>计划到达 (STA):</strong> ${formatInCurrentZone(flight.sta)}</div>`;
            } else {
                if (flight.etd || flight.eta) {
                    tooltip += `<div><strong>预计起飞 (ETD):</strong> ${formatInCurrentZone(flight.etd)}</div>`;
                    tooltip += `<div><strong>预计到达 (ETA):</strong> ${formatInCurrentZone(flight.eta)}</div>`;
                }

                if (flight.atd || flight.ata) {
                    tooltip += `<div><strong>实际起飞 (ATD):</strong> ${formatInCurrentZone(flight.atd)}</div>`;
                    tooltip += `<div><strong>实际到达 (ATA):</strong> ${formatInCurrentZone(flight.ata)}</div>`;
                }

                if (flight.atd_icao || flight.ata_icao) {
                    if (flight.atd_icao) {
                        tooltip += `<div><strong>ATD (Airborne):</strong> ${formatInCurrentZone(flight.atd_icao)}</div>`;
                    }
                    if (flight.ata_icao) {
                        tooltip += `<div><strong>ATA (Touch-down):</strong> ${formatInCurrentZone(flight.ata_icao)}</div>`;
                    }
                }

                if (flight.std && flight.sta) {
                    tooltip += `<hr><div style="font-size: 11px; color: #666;">对比计划时间:</div>`;
                    tooltip += `<div><strong>计划起飞:</strong> ${formatInCurrentZone(flight.std)}</div>`;
                    tooltip += `<div><strong>计划到达:</strong> ${formatInCurrentZone(flight.sta)}</div>`;
                }
            }

            tooltip += `<div class="vis-tooltip-hint">点击查看详情</div>`;
            tooltip += `</div>`;

            return tooltip;
        },
        [currentTimezone],
    );

    // --- Timeline 渲染与更新 ---
    // preservedWindow: 时区切换重建时保留用户当前缩放窗口，避免 fit-all 把条压成细线
    const renderVisTimeline = useCallback(
        (forceRender = false, preservedWindow = null) => {
            if (!timelineContainer.current || !Timeline || !DataSet) {
                setVisError("时间轴库未完全加载，请稍后或刷新重试。");
                return;
            }
            setVisError(null);

            try {
                if (!filteredFlights || filteredFlights.length === 0) {
                    if (timelineInstance.current) {
                        timelineItems.current.clear();
                        timelineGroups.current.clear();
                        timelineInstance.current.setItems(timelineItems.current);
                        timelineInstance.current.setGroups(timelineGroups.current);
                    }
                    return;
                }

                const aircraftMap = new Map();
                filteredFlights.forEach((flight) => {
                    const groupKey = flight.aircraft_reg || "UNASSIGNED";
                    if (!aircraftMap.has(groupKey)) aircraftMap.set(groupKey, []);
                    aircraftMap.get(groupKey).push(flight);
                });

                const groupsData = [];
                Array.from(aircraftMap.keys()).forEach((groupKey) => {
                    const aircraftName = groupKey === "UNASSIGNED" ? "未分配飞机" : groupKey;

                    if (timelineViewMode === TIMELINE_VIEW_MODES.BOTH) {
                        groupsData.push({
                            id: `${groupKey}_PLAN`,
                            content: `<div class="aircraft-group-label">
                                <span class="aircraft-name">${aircraftName}</span>
                                <span class="timeline-type plan">
                                    <span class="anticon anticon-clock-circle"></span>计划
                                </span>
                            </div>`,
                            className: "aircraft-plan-group",
                        });

                        groupsData.push({
                            id: `${groupKey}_ACTUAL`,
                            content: `<div class="aircraft-group-label">
                                <span class="timeline-type actual">
                                    <span class="anticon anticon-thunderbolt"></span>实际执行
                                </span>
                            </div>`,
                            className: "aircraft-actual-group",
                        });
                    } else if (timelineViewMode === TIMELINE_VIEW_MODES.PLAN_ONLY) {
                        groupsData.push({
                            id: `${groupKey}_PLAN`,
                            content: `<div class="aircraft-group-label">
                                <span class="aircraft-name">${aircraftName}</span>
                                <span class="timeline-type plan">
                                    <span class="anticon anticon-clock-circle"></span>计划时间
                                </span>
                            </div>`,
                            className: "aircraft-plan-group single-mode",
                        });
                    } else if (timelineViewMode === TIMELINE_VIEW_MODES.ACTUAL_ONLY) {
                        groupsData.push({
                            id: `${groupKey}_ACTUAL`,
                            content: `<div class="aircraft-group-label">
                                <span class="aircraft-name">${aircraftName}</span>
                                <span class="timeline-type actual">
                                    <span class="anticon anticon-thunderbolt"></span>执行时间
                                </span>
                            </div>`,
                            className: "aircraft-actual-group single-mode",
                        });
                    }
                });
                timelineGroups.current.clear();
                timelineGroups.current.add(groupsData);

                const itemsData = [];
                let minDate = dayjs().add(1, "year");
                let maxDate = dayjs().subtract(1, "year");

                aircraftMap.forEach((flightList, groupKey) => {
                    const sortedFlightList = flightList.sort((a, b) => dayjs(a.std).diff(dayjs(b.std)));

                    const { planOverlapIds: planOverlappingFlightIds, actualOverlapIds: actualOverlappingFlightIds } = resolveTimelineOverlapIds(sortedFlightList);

                    sortedFlightList.forEach((flight) => {
                        const isPlanOverlapping = planOverlappingFlightIds.has(flight.id);
                        const isActualOverlapping = actualOverlappingFlightIds.has(flight.id);

                        const shouldCreatePlanItem = timelineViewMode === TIMELINE_VIEW_MODES.BOTH || timelineViewMode === TIMELINE_VIEW_MODES.PLAN_ONLY;
                        const shouldCreateActualItem = timelineViewMode === TIMELINE_VIEW_MODES.BOTH || timelineViewMode === TIMELINE_VIEW_MODES.ACTUAL_ONLY;

                        const planTimes = resolveForwardTimelineTimes(flight, "plan");
                        const planStartTime = planTimes.departureTime;
                        const planEndTime = planTimes.arrivalTime;

                        if (shouldCreatePlanItem && planStartTime && planEndTime && dayjs.utc(planEndTime).isAfter(dayjs.utc(planStartTime))) {
                            const planStartMoment = dayjs.utc(planStartTime);
                            const planEndMoment = dayjs.utc(planEndTime);
                            if (planStartMoment.isBefore(minDate)) minDate = planStartMoment;
                            if (planEndMoment.isAfter(maxDate)) maxDate = planEndMoment;

                            const planColor = isPlanOverlapping ? "#ff4d4f" : "#8c8c8c";

                            itemsData.push({
                                id: `flight_${flight.id}_plan`,
                                group: `${groupKey}_PLAN`,
                                start: planStartMoment.toDate(),
                                end: planEndMoment.toDate(),
                                content: `<div class="flight-content plan">
                                    <span class="flight-number">${flight.flight_number}</span>
                                    <span class="route">${flight.departure_airport}→${flight.arrival_airport}</span>
                                </div>`,
                                title: createFlightTooltip(flight, isPlanOverlapping, "plan"),
                                className: `flight-block flight-plan ${isPlanOverlapping ? "flight-time-overlap" : ""}`,
                                style: `border-color: ${planColor}; background-color: ${planColor};`,
                                flight: flight,
                                type: "range",
                                timelineType: "plan",
                                isOverlapping: isPlanOverlapping,
                            });
                        }

                        const actualTimes = resolveForwardTimelineTimes(flight, "actual");
                        const actualStartTime = actualTimes.departureTime;
                        const actualEndTime = actualTimes.arrivalTime;

                        if (shouldCreateActualItem && actualStartTime && dayjs.utc(actualStartTime).isValid()) {
                            const actualStartMoment = dayjs.utc(actualStartTime);

                            const flightStatus = flight.status || FLIGHT_STATUS.SCHEDULED;
                            let actualColor = FLIGHT_STATUS_COLORS[flightStatus] || FLIGHT_STATUS_COLORS.default;
                            let actualLabel = "预计";

                            if (flight.atd && flight.ata) {
                                actualColor = FLIGHT_STATUS_COLORS[FLIGHT_STATUS.COMPLETED];
                                actualLabel = FLIGHT_STATUS_LABELS[FLIGHT_STATUS.COMPLETED];
                            } else if (flight.atd || flight.ata) {
                                actualColor = FLIGHT_STATUS_COLORS[FLIGHT_STATUS.DEPARTED];
                                actualLabel = FLIGHT_STATUS_LABELS[FLIGHT_STATUS.DEPARTED];
                            } else {
                                actualLabel = FLIGHT_STATUS_LABELS[flightStatus] || "预计";
                            }

                            if (isActualOverlapping) {
                                actualColor = "#ff4d4f";
                            }

                            const isInFlight = flight.atd && !flight.ata;
                            const etaIsForward = flight.eta && dayjs.utc(flight.eta).isAfter(actualStartMoment);
                            const shouldSplitInFlightBlock = isInFlight && !etaIsForward && flight.std && flight.sta;

                            if (shouldSplitInFlightBlock) {
                                const currentTime = dayjs.utc();
                                const plannedStartTime = dayjs.utc(flight.std);
                                const plannedEndTime = dayjs.utc(flight.sta);
                                const plannedFlightDuration = plannedEndTime.diff(plannedStartTime, "minute");

                                const actualFlightTime = currentTime.diff(actualStartMoment, "minute");
                                const remainingFlightTime = Math.max(0, plannedFlightDuration - actualFlightTime);
                                const estimatedEndTime = currentTime.add(remainingFlightTime, "minute");

                                if (actualStartMoment.isBefore(minDate)) minDate = actualStartMoment;
                                if (estimatedEndTime.isAfter(maxDate)) maxDate = estimatedEndTime;

                                const totalDuration = actualFlightTime + remainingFlightTime;
                                const flownPercentage = totalDuration > 0 ? Math.min(100, (actualFlightTime / totalDuration) * 100) : 0;

                                itemsData.push({
                                    id: `flight_${flight.id}_actual_combined`,
                                    group: `${groupKey}_ACTUAL`,
                                    start: actualStartMoment.toDate(),
                                    end: estimatedEndTime.toDate(),
                                    content: `<div class="flight-content actual">
                                        <span class="flight-number">${flight.flight_number}</span>
                                        <span class="route">${flight.departure_airport}→${flight.arrival_airport}</span>
                                        <span class="status-badge">${FLIGHT_STATUS_LABELS[FLIGHT_STATUS.DEPARTED]}</span>
                                    </div>`,
                                    title:
                                        createFlightTooltip(flight, isActualOverlapping, "actual") +
                                        `<div style="border-top: 1px solid #ddd; margin-top: 8px; padding-top: 8px;">
                                               <strong>飞行状态:</strong> 已飞行 ${Math.floor(actualFlightTime / 60)}小时${actualFlightTime % 60}分钟<br/>
                                               <strong>预计剩余:</strong> ${Math.floor(remainingFlightTime / 60)}小时${remainingFlightTime % 60}分钟
                                           </div>`,
                                    className: `flight-block flight-actual flight-in-flight ${isActualOverlapping ? "flight-time-overlap" : ""}`,
                                    style: `
                                        --flown-percentage: ${flownPercentage}%;
                                        border-color: ${actualColor}; 
                                        background-color: ${actualColor};
                                    `,
                                    flight: flight,
                                    type: "range",
                                    timelineType: "actual",
                                    isOverlapping: isActualOverlapping,
                                });
                            } else if (actualEndTime && dayjs.utc(actualEndTime).isAfter(actualStartMoment)) {
                                const actualEndMoment = dayjs.utc(actualEndTime);
                                if (actualStartMoment.isBefore(minDate)) minDate = actualStartMoment;
                                if (actualEndMoment.isAfter(maxDate)) maxDate = actualEndMoment;

                                itemsData.push({
                                    id: `flight_${flight.id}_actual`,
                                    group: `${groupKey}_ACTUAL`,
                                    start: actualStartMoment.toDate(),
                                    end: actualEndMoment.toDate(),
                                    content: `<div class="flight-content actual">
                                        <span class="flight-number">${flight.flight_number}</span>
                                        <span class="route">${flight.departure_airport}→${flight.arrival_airport}</span>
                                        <span class="status-badge">${actualLabel}</span>
                                    </div>`,
                                    title: createFlightTooltip(flight, isActualOverlapping, "actual"),
                                    className: `flight-block flight-actual flight-status-${flightStatus.toLowerCase()} ${isActualOverlapping ? "flight-time-overlap" : ""}`,
                                    style: `border-color: ${actualColor}; background-color: ${actualColor};`,
                                    flight: flight,
                                    type: "range",
                                    timelineType: "actual",
                                    isOverlapping: isActualOverlapping,
                                });
                            }
                        }
                    });
                });
                timelineItems.current.clear();
                timelineItems.current.add(itemsData);

                const baseTimelineOptions = {
                    orientation: "top",
                    stack: false,
                    stackSubgroups: true,
                    horizontalScroll: true,
                    verticalScroll: true,
                    zoomKey: "ctrlKey",
                    margin: { item: { horizontal: 5, vertical: 2 }, axis: 10 },
                    editable: false,
                    showCurrentTime: true,
                    tooltip: {
                        followMouse: false,
                        overflowMethod: "cap",
                        template: (item) => item.title || "",
                        delay: 1000,
                    },
                    snap: (date) => Math.round(date / (60 * 60 * 1000)) * (60 * 60 * 1000),
                    groupHeightMode: "fixed",
                    moment: function (date) {
                        return moment(date).tz(resolveVisTimelineTimezone(currentTimezone));
                    },
                };

                let timelineOptions;

                if (!timelineInstance.current || forceRender) {
                    const windowBounds = resolveTimelineWindowOnRecreate({
                        preservedWindow: itemsData.length > 0 ? preservedWindow : null,
                        minDate: itemsData.length > 0 ? minDate : null,
                        maxDate: itemsData.length > 0 ? maxDate : null,
                    });
                    timelineOptions = {
                        ...baseTimelineOptions,
                        start: windowBounds.start,
                        end: windowBounds.end,
                    };
                } else {
                    // Soft updates must not replace moment — timezone changes recreate the instance instead
                    const optionsWithoutMoment = { ...baseTimelineOptions };
                    delete optionsWithoutMoment.moment;
                    timelineOptions = optionsWithoutMoment;
                }

                if (forceRender && timelineInstance.current) {
                    timelineInstance.current.destroy();
                    timelineInstance.current = null;
                }

                // Ensure container is measurable before creating vis-timeline (hidden containers get zero size)
                if (timelineContainer.current) {
                    timelineContainer.current.style.display = "block";
                }

                if (!timelineInstance.current) {
                    timelineInstance.current = new Timeline(timelineContainer.current, timelineItems.current, timelineGroups.current, timelineOptions);
                    timelineInstance.current.on("click", (properties) => {
                        if (properties.item) {
                            const clickedItem = timelineItems.current.get(properties.item);
                            if (clickedItem?.flight) {
                                toggleModal(MODAL_KEYS.ENHANCED_FLIGHT_TIMES, true, clickedItem.flight);
                            }
                        }
                    });

                    appliedTimezoneRef.current = currentTimezone;
                    setTimelineReady(true);

                    safeTimeout(() => {
                        if (timelineInstance.current) {
                            timelineInstance.current.redraw();
                        }
                    }, 300);
                } else {
                    timelineInstance.current.setOptions(timelineOptions);
                    timelineInstance.current.setGroups(timelineGroups.current);
                    timelineInstance.current.setItems(timelineItems.current);
                    timelineInstance.current.redraw();
                    setTimelineReady(true);
                }
            } catch (error) {
                console.error("渲染时间轴失败:", error);
                setVisError(`渲染时间轴失败: ${error.message}`);
                setTimelineReady(false);
            }
        },
        [filteredFlights, createFlightTooltip, toggleModal, currentTimezone, timelineViewMode, safeTimeout],
    );

    // 主 Effect: 数据就绪时渲染时间轴；时区变更必须强制重建实例，并保留当前缩放窗口
    useEffect(() => {
        if (loading || !filteredFlights?.length || !timelineContainer.current) {
            return;
        }

        const timezoneChanged = shouldForceRecreateTimelineForTimezone(appliedTimezoneRef.current, currentTimezone);
        if (timezoneChanged && timelineInstance.current) {
            let preservedWindow = null;
            try {
                preservedWindow = timelineInstance.current.getWindow();
            } catch (error) {
                console.warn("读取时间轴窗口失败，将回退到自适应范围:", error);
            }
            timelineInstance.current.destroy();
            timelineInstance.current = null;
            timelineContainer.current.style.display = "block";
            renderVisTimeline(true, preservedWindow);
            return;
        }

        renderVisTimeline(false);
    }, [loading, filteredFlights, currentTimezone, renderVisTimeline]);

    const handleForceRedraw = useCallback(() => {
        if (timelineInstance.current) {
            timelineInstance.current.destroy();
            timelineInstance.current = null;
        }

        if (timelineContainer.current) {
            timelineContainer.current.style.display = "block";
        }

        safeTimeout(() => {
            renderVisTimeline(true);
        }, 50);
    }, [renderVisTimeline, safeTimeout]);

    const handleZoomIn = useCallback(() => {
        if (timelineInstance.current) {
            timelineInstance.current.zoomIn(0.2);
        }
    }, []);

    const handleZoomOut = useCallback(() => {
        if (timelineInstance.current) {
            timelineInstance.current.zoomOut(0.2);
        }
    }, []);

    // 组件卸载时清理所有资源
    useEffect(() => {
        return () => {
            pendingTimers.current.forEach((id) => clearTimeout(id));
            pendingTimers.current.clear();

            if (timelineInstance.current) {
                timelineInstance.current.destroy();
                timelineInstance.current = null;
            }
        };
    }, []);

    const handleAircraftSelectionChange = (selectedValues) => {
        if (selectedValues.length === 0 || selectedValues.includes("ALL")) {
            setSelectedAircraft(["ALL"]);
        } else {
            setSelectedAircraft(selectedValues);
        }
    };

    // Keep container visible whenever there is flight data so vis-timeline can measure layout.
    // Gating on timelineReady caused display:none during recreate and made the Gantt appear to vanish.
    const hasFlightData = !loading && filteredFlights && filteredFlights.length > 0;
    const showTimelineContainer = hasFlightData && !visError;
    const showEmpty = !loading && (!filteredFlights || filteredFlights.length === 0);

    return (
        <div className="flight-timeline-vis-container">
            {isDataTruncated && (
                <Alert
                    type="warning"
                    showIcon
                    banner
                    message={`当前仅显示 ${flights.length} / ${pagination.total} 条航班，请缩小日期范围或添加筛选条件以查看完整数据`}
                    style={{ marginBottom: 8 }}
                />
            )}
            <div className="timeline-toolbar">
                <Space wrap size="middle">
                    <Space>
                        <FilterOutlined />
                        <Text>飞机筛选:</Text>
                        <Select mode="multiple" allowClear style={{ minWidth: 250 }} placeholder="选择飞机 (默认全部)" value={selectedAircraft.includes("ALL") ? [] : selectedAircraft} onChange={handleAircraftSelectionChange} maxTagCount="responsive">
                            {availableAircraft.map((ac) => (
                                <Option key={ac.id} value={ac.id}>
                                    {ac.name}
                                </Option>
                            ))}
                        </Select>
                    </Space>
                    <Divider type="vertical" />
                    <Space>
                        <EyeOutlined />
                        <Text>视图模式:</Text>
                        <Radio.Group value={timelineViewMode} onChange={(e) => setTimelineViewMode(e.target.value)} size="small" buttonStyle="solid">
                            <Radio.Button value={TIMELINE_VIEW_MODES.BOTH}>
                                <ClockCircleOutlined /> <ThunderboltOutlined /> 双行
                            </Radio.Button>
                            <Radio.Button value={TIMELINE_VIEW_MODES.PLAN_ONLY}>
                                <ClockCircleOutlined /> 计划
                            </Radio.Button>
                            <Radio.Button value={TIMELINE_VIEW_MODES.ACTUAL_ONLY}>
                                <ThunderboltOutlined /> 执行
                            </Radio.Button>
                        </Radio.Group>
                    </Space>
                    <Space>
                        <Tooltip title="放大">
                            <Button icon={<PlusOutlined />} onClick={handleZoomIn} size="small" disabled={!timelineReady || loading} />
                        </Tooltip>
                        <Tooltip title="缩小">
                            <Button icon={<MinusOutlined />} onClick={handleZoomOut} size="small" disabled={!timelineReady || loading} />
                        </Tooltip>
                        <Tooltip title="刷新时间轴视图">
                            <Button icon={<ReloadOutlined />} onClick={handleForceRedraw} size="small" disabled={loading} />
                        </Tooltip>
                    </Space>
                    <Text type="secondary" style={{ fontSize: "12px" }}>
                        {TIMELINE_VIEW_MODE_LABELS[timelineViewMode]} {loading ? "" : `(已加载 ${filteredFlights?.length || 0} 条航班)`}
                    </Text>
                </Space>
            </div>
            <Divider style={{ margin: "12px 0" }} />
            <Spin spinning={loading} tip="加载航班数据中..." wrapperClassName="timeline-spin-wrapper">
                <div className="timeline-content-area">
                    {visError && (
                        <div className="vis-error-message">
                            <WarningTwoTone twoToneColor="#eb2f96" /> {visError}
                        </div>
                    )}
                    {!visError && showEmpty && <Empty description="没有符合当前筛选条件的航班数据" style={{ marginTop: "50px" }} />}

                    <div ref={timelineContainer} className="vis-timeline-container" style={{ display: showTimelineContainer ? "block" : "none", height: "calc(100vh - 610px)", minHeight: "250px" }} />

                    {!loading && !visError && filteredFlights?.length > 0 && !timelineReady && (
                        <div className="timeline-loading-message" style={{ textAlign: "center", margin: "30px 0" }}>
                            <Text type="secondary">时间轴准备中...如未显示，请点击上方刷新按钮</Text>
                        </div>
                    )}

                    {!loading && !visError && timelineReady && (
                        <div className="timeline-legend">
                            <div style={{ fontWeight: "bold", marginBottom: "8px" }}>图例说明:</div>
                            <Space wrap size={[16, 8]}>
                                <Space align="center">
                                    <div className="legend-color" style={{ backgroundColor: "#8c8c8c66", border: "1px solid #8c8c8c" }} />
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        计划时间 (有预计跟预计)
                                    </Text>
                                </Space>
                                <Space align="center">
                                    <div className="legend-color" style={{ backgroundColor: "#52c41aaa", border: "1px solid #52c41a" }} />
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        执行时间 (ETD/ETA/ATD/ATA)
                                    </Text>
                                </Space>

                                <Divider type="vertical" style={{ height: "16px", margin: "0 8px" }} />

                                <Space align="center">
                                    <div className="legend-color" style={{ backgroundColor: `${FLIGHT_STATUS_COLORS[FLIGHT_STATUS.COMPLETED]}aa`, border: `1px solid ${FLIGHT_STATUS_COLORS[FLIGHT_STATUS.COMPLETED]}` }} />
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        已完成
                                    </Text>
                                </Space>
                                <Space align="center">
                                    <div className="legend-color" style={{ backgroundColor: `${FLIGHT_STATUS_COLORS[FLIGHT_STATUS.DEPARTED]}aa`, border: `1px solid ${FLIGHT_STATUS_COLORS[FLIGHT_STATUS.DEPARTED]}` }} />
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        进行中
                                    </Text>
                                </Space>
                                <Space align="center">
                                    <div className="legend-color" style={{ backgroundColor: `${FLIGHT_STATUS_COLORS[FLIGHT_STATUS.DELAYED]}aa`, border: `1px solid ${FLIGHT_STATUS_COLORS[FLIGHT_STATUS.DELAYED]}` }} />
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        延误
                                    </Text>
                                </Space>
                                <Space align="center">
                                    <div className="legend-color flight-time-overlap-legend" />
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        时间重叠
                                    </Text>
                                </Space>
                                <Space align="center">
                                    <div
                                        className="legend-color"
                                        style={{
                                            background: `linear-gradient(to right, ${FLIGHT_STATUS_COLORS[FLIGHT_STATUS.DEPARTED]} 0%, ${FLIGHT_STATUS_COLORS[FLIGHT_STATUS.DEPARTED]} 60%, ${FLIGHT_STATUS_COLORS[FLIGHT_STATUS.DEPARTED]}66 60%, ${FLIGHT_STATUS_COLORS[FLIGHT_STATUS.DEPARTED]}66 100%)`,
                                            border: `1px solid ${FLIGHT_STATUS_COLORS[FLIGHT_STATUS.DEPARTED]}`,
                                            borderRight: `1px dashed ${FLIGHT_STATUS_COLORS[FLIGHT_STATUS.DEPARTED]}`,
                                        }}
                                    />
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        飞行中分段显示
                                    </Text>
                                </Space>
                            </Space>

                            <div style={{ marginTop: "8px", fontSize: "11px", color: "#666" }}>
                                提示：{timelineViewMode === TIMELINE_VIEW_MODES.BOTH ? "每架飞机显示两行 - 上行为计划时间（灰色），下行为执行时间（彩色）" : "单行显示模式"}。 飞行中航班无预计到达时间时将分段显示：实心部分为已飞行时间，虚线部分为基于计划的预计剩余时间。点击任意航班可查看详细信息。
                            </div>
                        </div>
                    )}
                </div>
            </Spin>
        </div>
    );
};

export default FlightTimelineView;
